"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ChevronDown, ChevronUp, LogOut, Loader2, Edit3 } from "lucide-react";
import useAuth from "@/hooks/auth/useAuth";
import { UserProfileDialog } from "./user-profile-dialog";

interface AccountSwitcherProps {
  expanded?: boolean;
}

export function AccountSwitcher({ expanded = true }: AccountSwitcherProps) {
  const { user, signOut, loading } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
    } catch (error) {
      console.error("Sign out failed:", error);
      setIsSigningOut(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="border-t">
        <div className="p-3">
          <div className="flex items-center space-x-3">
            <div className="h-8 w-8 rounded-full bg-slate-200 animate-pulse" />
            <div className="space-y-1">
              <div className="h-3 w-20 bg-slate-200 rounded animate-pulse" />
              <div className="h-2 w-24 bg-slate-200 rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Get user display name from various possible sources
  const getUserDisplayName = () => {
    // The auth provider now properly constructs the name from first_name/last_name
    // so we can use user.name directly, which will be in "First Last" format
    if (user.name && user.name !== user.email.split("@")[0]) {
      return user.name;
    }

    // Try user metadata as fallback (in case of edge cases)
    if (user.user_metadata?.display_name) {
      return user.user_metadata.display_name;
    }

    // Try constructing from first/last name in metadata as secondary fallback
    if (user.user_metadata?.first_name || user.user_metadata?.last_name) {
      const firstName = user.user_metadata.first_name || "";
      const lastName = user.user_metadata.last_name || "";
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName) return fullName;
    }

    // Last resort: use email prefix
    return user.email.split("@")[0];
  };

  const displayName = getUserDisplayName();
  const initials = displayName
    .split(" ")
    .map((name) => name.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);

  // Collapsed sidebar state - show minimal user info
  if (!expanded) {
    return (
      <div className="border-t">
        <div className="p-3">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-blue-500 text-white text-sm">{initials}</AvatarFallback>
          </Avatar>
        </div>
      </div>
    );
  }

  // Expanded sidebar state - show full user section with expandable options
  return (
    <div className="border-t">
      {/* Main user info section */}
      <Button
        variant="ghost"
        className="w-full h-auto p-3 justify-between hover:bg-slate-100"
        onClick={toggleExpanded}
        disabled={isSigningOut}
      >
        <div className="flex items-center space-x-3 min-w-0">
          <Avatar className="h-8 w-8 flex-shrink-0">
            <AvatarFallback className="bg-blue-500 text-white text-sm">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col items-start min-w-0">
            <span className="text-sm font-medium text-left truncate w-full">{displayName}</span>
            <span className="text-xs text-slate-500 text-left truncate w-full">{user.email}</span>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-slate-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-slate-500 flex-shrink-0" />
        )}
      </Button>

      {/* Expanded options */}
      {isExpanded && (
        <div className="bg-slate-50 border-t">
          {/* Profile Edit Dialog */}
          <UserProfileDialog />

          {/* Sign out option */}
          <Button
            variant="ghost"
            className="w-full h-10 justify-start px-6 text-slate-600 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleSignOut}
            disabled={isSigningOut}
          >
            {isSigningOut ? <Loader2 className="h-4 w-4 mr-3 animate-spin" /> : <LogOut className="h-4 w-4 mr-3" />}
            <span className="text-sm">{isSigningOut ? "Signing out..." : "Log out"}</span>
          </Button>

          {/* Emergency sign-out option - only show if regular sign out failed */}
          {isSigningOut && (
            <div className="px-6 py-2">
              <button
                onClick={() => {
                  console.log("Emergency sign out - forcing redirect");
                  window.location.href = "/login";
                }}
                className="text-xs text-red-600 hover:text-red-800 underline"
              >
                Sign out stuck? Click here
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
